import {sourceHelper} from "./source.helper";
import {__isKeyAvailable} from "./commons/utility.helper";

export const orderHelper = {
    getOrderCost,
    getFormattedPrice,
    getUsagesData,
    getOrderLabels
};

// expected usage types
const usageType = {
    // name should match the response key identifier
    ORDER_GIST: {
        "name": "ORDER_GIST",
        "unit": "minutes"
    },
    ORDER_GIST_DOCS: {
        "name": "ORDER_GIST_DOCS",
        "unit": "pages"
    },
    ORDER_GIST_TEXT: {
        "name": "ORDER_GIST_TEXT",
        "unit": "minutes"
    },
    STORAGE: {
        "name": "STORAGE",
        "unit": "MB"
    }
};

/**
 * Returns Cost for the order
 *
 * @param usagesSummary order usage summary
 * @returns {number} total cost
 */
function getOrderCost (usagesSummary) {
    let cost = 0;
    for (const identifier of Object.keys(usagesSummary)) {
        const currentUsage = usagesSummary[identifier];
        const {request} = currentUsage; // get the current request usage data
        if(__isKeyAvailable(request, "cost")) {
            cost += request.cost;
        }
    }
    return cost;
}

/**
 * Returns formatted price
 *
 * @param priceInCents the given amount in cents
 * @returns {string} formatted price
 */
function getFormattedPrice(priceInCents){
    const currencySymbol = "$";     // set currency symbol
    const decimalPlaces = 2;        // desired decimal places
    
    // ceil to nearest integer penny, convert to dollar & fix decimal places to 2 places
    const formattedValue = (Math.ceil(priceInCents) / 100).toFixed(decimalPlaces);
    
    return currencySymbol + formattedValue;
}

/**
 * check if usage request is available
 *
 * @param usage usage detail instance
 * @returns {boolean} usage request is available
 */
const isUsageRequestAvailable = (usage) => {
    return __isKeyAvailable(usage, "request", "usage");
}

/**
 * Return labels for order
 *
 * @param source source for which label needs to be generated (running-time, quantity, hosting-size, etc)
 * @returns {*[]} labels for order
 */
function getOrderLabels(source) {
    const orderLabels = [];
    
    const usagesSummaryList = source.usage.summary;
    for (const identifier of Object.keys(usagesSummaryList)) {
        if (!Object.keys(usageType).includes(identifier)) { continue; } // ignore unexpected details
        
        const currentUsage = usagesSummaryList[identifier];
        if(isUsageRequestAvailable(currentUsage)) {
            let labelValue = "";
            switch(identifier) {
                case usageType.ORDER_GIST_TEXT.name:
                case usageType.ORDER_GIST.name:
                    labelValue = sourceHelper.formatSourceTimeDuration(source.info.duration);
                    break;
                
                case usageType.ORDER_GIST_DOCS.name:
                    const duration = source.info.duration
                    labelValue = duration + ((duration > 1) ? " Pages" : " Page");
                    break;
                
                case usageType.STORAGE.name:
                    labelValue = currentUsage.request.usage + " " + usageType.STORAGE.unit
                    break;
                
                default:
                    break;
            }
            const label = {
                "key": identifier,
                "value": (orderLabels.length > 0 ? ", " : "") + labelValue  // added comma separator in case of multiple labels
            }
            orderLabels.push(label);
        }
    }
    return orderLabels;
}

/**
 * Return usages data
 *
 * @param usagesSummaryList usages summary
 * @returns {*[]} Usages data for identifiers
 */
function getUsagesData(usagesSummaryList) {
    const usagesProgressData = [];
    
    // Calculate the remaining usage
    const calculateRemainingUsage = (usage) => {
        const {included} = usage;
        const {used, total} = included;
        return (used < total) ? (total - used) : 0;
    };
    
    // Calculate the usage progress
    const calculateUsageProgress = (usage, orderUsage = 0) => {
        const {included} = usage;
        const {used, total} = included;
        return (used < total) ? ((used + orderUsage) / total * 100) : 100;
    }
    
    // Get the usage request size
    const getUsageRequestSize = (usage) => (isUsageRequestAvailable(usage)) ? usage.request.usage : 0;
    
    for (const identifier of Object.keys(usagesSummaryList)) {
        if (!Object.keys(usageType).includes(identifier)) { continue; } // ignore unexpected details
        
        const currentUsage = usagesSummaryList[identifier];
        const remainingIncludedUsage = calculateRemainingUsage(currentUsage);
        
        // Prepare usage for current identifier, if relevant
        if(isUsageRequestAvailable(currentUsage)) {
            let labelRemaining, labelTotal, labelUnit;
            switch (identifier) {
                case usageType.STORAGE.name:
                    labelRemaining = (remainingIncludedUsage / 1000).toFixed(1) + " GB";
                    labelTotal = (currentUsage.included.total / 1000).toFixed(1) + " GB";
                    labelUnit = "storage";
                    break;
                default:
                    labelRemaining = remainingIncludedUsage;
                    labelTotal = currentUsage.included.total;
                    labelUnit = usageType[identifier].unit;
                    break;
            }
            const currentUsageData = {
                "type": identifier,
                "postUsage": calculateUsageProgress(currentUsage, getUsageRequestSize(currentUsage)),
                "preUsage": calculateUsageProgress(currentUsage),
                "label": {
                    "unit": labelUnit, "remaining": labelRemaining, "total": labelTotal
                }
            }
            usagesProgressData.push(currentUsageData);
        }
    }
    return usagesProgressData;
}